// Phase 1C — Canvas 2D renderer.
// H2.3 WO#5 (Final Obsidian Visual Convergence) — Creative director pass.
//
// Presentation refined. World geometry, camera math, hit-testing,
// coordinate mapping preserved verbatim. Zone rectangles come from the
// canonical resolved-disjoint manifest and NEVER from hardcoded coords.
import type { Camera } from "./camera";
import { ownerColor, worldToScreen } from "./camera";
import { lod0FadeOpacity } from "../domain/lod0-brand-image";
import { computeBrandImageDrawGeometry } from "./brand-image-geometry";
import type { GridSelection } from "../lib/grid-selection";
import type { Parcel } from "./types";
import type { ZoneManifest, ZoneRole } from "../lib/zone-manifest";

export interface SelectionOverlayInput {
  selection: GridSelection;
  kind: "draft" | "committed";
}

export interface ZoneOverlayInput {
  manifest: ZoneManifest;
}

/** WO#5 v2 §ZONES — designed material palette (teal/coral).
 * Fills stay restrained (0.03–0.16 alpha) so the Grid remains readable.
 * Borders do the identity work at zone boundaries. Grid Zero carries
 * the coral nucleus language established by the hero's IsoWorld — the
 * user should recognise "this is the same world, now I can touch it". */
export const ZONE_STYLE: Record<
  ZoneRole,
  { fill: string; border: string; borderAlpha: number; inner?: string }
> = {
  STANDARD: {
    fill:   "rgba(166, 179, 196, 0.030)",
    border: "rgba(166, 179, 196, 0.14)",
    borderAlpha: 1,
  },
  PREMIUM: {
    fill:   "rgba(0, 240, 232, 0.10)",
    border: "rgba(60, 240, 240, 0.75)",
    borderAlpha: 1,
    inner:  "rgba(60, 240, 240, 0.22)",
  },
  VIP_CORE_SELLABLE: {
    fill:   "rgba(70, 150, 155, 0.10)",
    border: "rgba(90, 170, 175, 0.35)",
    borderAlpha: 1,
    inner:  "rgba(150, 255, 250, 0.32)",
  },
  LOCKED_GRID_ZERO: {
    fill:   "rgba(255, 90, 90, 0.22)",
    border: "rgba(255, 150, 150, 0.95)",
    borderAlpha: 1,
    inner:  "rgba(255, 140, 140, 0.44)",
  },
  LOCKED_RESERVE: {
    fill:   "rgba(90, 150, 210, 0.10)",
    border: "rgba(120, 160, 220, 0.42)",
    borderAlpha: 1,
    inner:  "rgba(120, 175, 235, 0.20)",
  },
};

/** WO-VRD-33 — SOLID (opaque) per-role colours for the Reveal overlay.
 * The ZONE_STYLE palette bakes low alpha into its colours, which would
 * multiply against globalAlpha and render near-invisible. These solids
 * let globalAlpha alone drive the fluorescent intensity. */
export const REVEAL_SOLID: Record<ZoneRole, string> = {
  STANDARD: "rgb(150, 170, 200)",
  PREMIUM: "rgb(40, 240, 235)",
  VIP_CORE_SELLABLE: "rgb(130, 255, 245)",
  LOCKED_GRID_ZERO: "rgb(255, 110, 120)",
  LOCKED_RESERVE: "rgb(110, 175, 240)",
};

/**
 * WO-VRD-33 — "Reveal Areas" fluorescent overlay. For each canonical
 * zone region, paints an additive pulsing fill + a glowing rounded
 * border in the zone's own palette colour so the territory taxonomy
 * reads at a glance. `pulse` is a 0..1 breathing factor. When
 * `hoverRole` is set (a legend row is hovered) ONLY that role glows at
 * full strength while the others fade back, so the user can isolate a
 * single category. Presentation-only: consumes the SAME canonical
 * regions the base zone passes use; no geometry / hit-test involvement.
 */
export function paintZoneReveal(
  ctx: CanvasRenderingContext2D,
  camera: Camera,
  manifest: ZoneManifest,
  cssWidth: number,
  cssHeight: number,
  pulse: number,
  hoverRole: ZoneRole | null,
): void {
  // WO-VRD-33 (Claudio) — pressing "Reveal Areas" alone must NOT light up
  // any zone on the grid; it only surfaces the legend. Zones illuminate
  // STRICTLY on legend hover. So with no hovered role we paint nothing.
  if (hoverRole == null) return;
  ctx.save();
  ctx.globalCompositeOperation = "lighter";
  for (const r of manifest.regions) {
    // Only the hovered category illuminates; every other zone stays dark.
    if (r.zone !== hoverRole) continue;
    const style = ZONE_STYLE[r.zone as ZoneRole];
    if (!style) continue;
    const solid = REVEAL_SOLID[r.zone as ZoneRole];
    const a = worldToScreen(camera, r.x, r.y);
    const b = worldToScreen(camera, r.x + r.w, r.y + r.h);
    const w = b.x - a.x;
    const h = b.y - a.y;
    if (w <= 2 || h <= 2) continue;
    if (a.x + w < 0 || a.y + h < 0 || a.x > cssWidth || a.y > cssHeight) continue;
    const focus = 1;
    const rad = Math.max(2, Math.min(w, h) * 0.12);
    // Pulsing fluorescent fill (opaque colour + globalAlpha drives it).
    ctx.globalAlpha = (0.34 + 0.26 * pulse) * focus;
    ctx.fillStyle = solid;
    fillRoundedRect(ctx, a.x, a.y, w, h, rad);
    // Glowing rounded border.
    ctx.globalAlpha = (0.78 + 0.22 * pulse) * focus;
    ctx.shadowColor = solid;
    ctx.shadowBlur = (12 + 20 * pulse) * (0.4 + 0.6 * focus);
    ctx.strokeStyle = solid;
    ctx.lineWidth = hoverRole === r.zone ? 3 : 2;
    strokeRoundedRect(
      ctx,
      Math.round(a.x) + 0.5,
      Math.round(a.y) + 0.5,
      Math.max(1, Math.round(w) - 1),
      Math.max(1, Math.round(h) - 1),
      rad,
    );
    ctx.shadowBlur = 0;
  }
  ctx.globalAlpha = 1;
  ctx.restore();
}

export interface RenderInput {
  ctx: CanvasRenderingContext2D;
  cssWidth: number;
  cssHeight: number;
  dpr: number;
  camera: Camera;
  parcels: Parcel[];
  hoveredId: number | null;
  selectedId: number | null;
  degraded: boolean;
  selectionOverlay?: SelectionOverlayInput | null;
  zoneOverlay?: ZoneOverlayInput | null;
  /**
   * WO-VRD-33 — "Reveal Areas" presentation toggle. When true, the
   * renderer paints an additional pulsing fluorescent highlight over the
   * canonical zone regions (Grid Zero / VIP Core / Premium / Reserve /
   * Standard) so the user can read the territory taxonomy at a glance.
   * Pure presentation: derives ONLY from the same canonical
   * `zoneOverlay` regions; camera math / hit-test / geometry untouched.
   */
  revealZones?: boolean;
  /**
   * WO-VRD-33 — when a legend category is hovered, only this zone role
   * glows at full strength (the others fade back). `null` = all zones on.
   */
  revealHoverRole?: ZoneRole | null;
  /**
   * LOD0 Brand Image presentation layer. Each item is one CURRENT operational
   * Territory (world-space parcel rectangles) plus its decoded image and the
   * server-authoritative decoded dimensions. Painted ABOVE parcels and BELOW
   * the hover/selection overlays, clipped to the EXACT parcel union, faded by
   * camera zoom. PRESENTATION ONLY: never consulted by hit-test / selection /
   * gesture / geometry. Empty / omitted = nothing drawn.
   */
  brandImages?: BrandImageLayerItem[];
  /**
   * PERF — frame quality. "fast" is used WHILE the camera is actively moving
   * (pan / zoom / inertia / tween): the expensive decorative Gaussian glows
   * (`shadowBlur`) are skipped so panning stays fluid. "full" (default) paints
   * every glow and is used the instant motion settles (a trailing repaint
   * restores full quality). Purely presentational — geometry/hit-test untouched.
   */
  quality?: "fast" | "full";
  /**
   * WO-CONTEST §BAND-FADE (Claudio) — intensity [0..1] for the 3 concentric
   * perimeter bands ONLY. Instead of the old binary on/off (skipped whenever
   * `quality === "fast"`), the shell animates this factor down toward 0 while
   * the camera is moving and back up toward 1 when it settles (~350ms medium
   * fade), so the bands "spegnimento/riaccensione" reads as a smooth glow
   * rather than an instant pop. Default 1 (full). Presentation only.
   */
  glowIntensity?: number;
}

/** One renderable brand image: current territory geometry + decoded texture. */
export interface BrandImageLayerItem {
  units: { x: number; y: number; w: number; h: number }[];
  image: CanvasImageSource;
  imageWidth: number;
  imageHeight: number;
}

/**
 * Paint the LOD0 brand image layer: aspect-fit into each CURRENT territory,
 * clipped to the EXACT parcel union (never the bounding box), faded by zoom.
 * Fully presentation-only and constant-cost per visible territory.
 */
export function drawBrandImages(
  ctx: CanvasRenderingContext2D,
  camera: Camera,
  items: readonly BrandImageLayerItem[],
): void {
  const opacity = lod0FadeOpacity(camera.scale);
  if (opacity <= 0) return; // >= LOD0 fade-end zoom → absent
  for (const item of items) {
    const geo = computeBrandImageDrawGeometry(camera, item.units, item.imageWidth, item.imageHeight);
    if (!geo || geo.clip.length === 0) continue;
    ctx.save();
    // EXACT union clip: one subpath per parcel rect → holes/L-shapes fall out.
    ctx.beginPath();
    for (const r of geo.clip) ctx.rect(r.x, r.y, r.w, r.h);
    ctx.clip();
    ctx.globalAlpha = opacity;
    ctx.imageSmoothingEnabled = true;
    try {
      ctx.drawImage(item.image, geo.fit.x, geo.fit.y, geo.fit.w, geo.fit.h);
    } catch {
      /* an undecoded/broken image source must never break the frame */
    }
    ctx.restore();

    // Faint fluorescent border marking the exact territory footprint the
    // image is installed on (very subtle — presentation only).
    ctx.save();
    ctx.beginPath();
    for (const r of geo.clip) ctx.rect(r.x, r.y, r.w, r.h);
    ctx.globalAlpha = opacity;
    ctx.lineWidth = 1;
    ctx.strokeStyle = "rgba(45, 182, 240, 0.22)";
    ctx.shadowColor = "rgba(45, 182, 240, 0.35)";
    ctx.shadowBlur = 4;
    ctx.stroke();
    ctx.restore();
  }
}

export function resizeCanvasForDpr(canvas: HTMLCanvasElement, dpr: number) {
  const rect = canvas.getBoundingClientRect();
  const w = Math.max(1, Math.floor(rect.width));
  const h = Math.max(1, Math.floor(rect.height));
  const bw = Math.max(1, Math.floor(w * dpr));
  const bh = Math.max(1, Math.floor(h * dpr));
  if (canvas.width !== bw) canvas.width = bw;
  if (canvas.height !== bh) canvas.height = bh;
  return { cssWidth: w, cssHeight: h };
}

/**
 * WO-P2 §TECH-SUBSTRATE — cached ambient pattern painted OUTSIDE the
 * canonical world rectangle. Pure decoration: circuit-like microlines,
 * cyan node dots, occasional coral signal specks. Deterministic (seeded
 * hash from index) so the same viewport always paints identically —
 * never confusable with parcels/zones/live signals. Cached on an
 * offscreen tile that is then repeated across the substrate band.
 *
 * Cache invalidation: keyed on DPR only (tile size is a constant CSS
 * dimension). The tile is opaque about world coordinates; it moves
 * with the canvas frame, not with camera pan — this is deliberate:
 * the substrate is *shell environment*, not *territory data*.
 */
const TECH_TILE_CSS = 240;
let _techTile: HTMLCanvasElement | null = null;
let _techTileDpr = 0;
function getTechTile(dpr: number): HTMLCanvasElement | null {
  if (_techTile && _techTileDpr === dpr) return _techTile;
  // Node/vitest test environments (renderer-parcel-visibility.test.ts
  // runs in a plain Node env with a fake canvas ctx) do not expose
  // `document`. The tech substrate is pure decoration — we skip it
  // silently and let the rest of the renderer produce its canonical
  // output for assertion.
  if (typeof document === "undefined") return null;
  const size = Math.floor(TECH_TILE_CSS * dpr);
  const c = document.createElement("canvas");
  c.width = size;
  c.height = size;
  const g = c.getContext("2d");
  if (!g) return c;
  g.scale(dpr, dpr);
  // Micro grid — very cool, very subtle.
  g.strokeStyle = "rgba(58, 200, 255, 0.045)";
  g.lineWidth = 1;
  for (let x = 20; x < TECH_TILE_CSS; x += 20) {
    g.beginPath();
    g.moveTo(x + 0.5, 0);
    g.lineTo(x + 0.5, TECH_TILE_CSS);
    g.stroke();
  }
  for (let y = 20; y < TECH_TILE_CSS; y += 20) {
    g.beginPath();
    g.moveTo(0, y + 0.5);
    g.lineTo(TECH_TILE_CSS, y + 0.5);
    g.stroke();
  }
  // Occasional slightly brighter cross-hair (like circuit vias).
  g.strokeStyle = "rgba(58, 200, 255, 0.14)";
  g.lineWidth = 1;
  const crossPositions: Array<[number, number]> = [
    [60, 100], [160, 60], [200, 180], [40, 200],
  ];
  for (const [cx, cy] of crossPositions) {
    g.beginPath();
    g.moveTo(cx - 4, cy + 0.5);
    g.lineTo(cx + 4, cy + 0.5);
    g.moveTo(cx + 0.5, cy - 4);
    g.lineTo(cx + 0.5, cy + 4);
    g.stroke();
  }
  // Cyan node dots
  g.fillStyle = "rgba(58, 200, 255, 0.55)";
  const nodes: Array<[number, number]> = [
    [60, 100], [160, 60], [200, 180], [40, 200],
    [120, 140], [80, 40],
  ];
  for (const [nx, ny] of nodes) g.fillRect(nx - 1, ny - 1, 2, 2);
  // Rare coral signal specks (data pulse feel — purely aesthetic).
  g.fillStyle = "rgba(255, 0, 68, 0.55)";
  g.fillRect(120 - 1, 140 - 1, 2, 2);
  g.fillRect(180 - 1, 20 - 1, 2, 2);
  _techTile = c;
  _techTileDpr = dpr;
  return c;
}

/**
 * Paint the tech substrate band around (but outside) the world.
 * Uses `createPattern` for O(1) blit cost regardless of surface area.
 * Alpha stays low so the pattern reads as *environment*, never as UI.
 */
export function paintTechSubstrate(
  ctx: CanvasRenderingContext2D,
  cssW: number, cssH: number, dpr: number,
  worldX: number, worldY: number, worldW: number, worldH: number,
): void {
  if (worldW <= 0 || worldH <= 0) return;
  const tile = getTechTile(dpr);
  if (!tile) return;
  const pat = ctx.createPattern(tile, "repeat");
  if (!pat) return;
  // Pattern coordinates are in device pixels — normalise by DPR
  // so the tile spacing reads consistently regardless of screen.
  // (canvas has already been setTransform'd to dpr,dpr in caller.)
  ctx.save();
  // Clip to the four rectangles around the world (top, bottom, left, right)
  ctx.beginPath();
  ctx.rect(0, 0, cssW, Math.max(0, worldY));                                        // top band
  ctx.rect(0, worldY + worldH, cssW, Math.max(0, cssH - (worldY + worldH)));         // bottom band
  ctx.rect(0, worldY, Math.max(0, worldX), worldH);                                  // left band
  ctx.rect(worldX + worldW, worldY, Math.max(0, cssW - (worldX + worldW)), worldH);  // right band
  ctx.clip();
  ctx.fillStyle = pat as CanvasPattern;
  ctx.fillRect(0, 0, cssW, cssH);
  // Very soft radial darken from the world outward — separates world
  // from substrate without a hard vignette.
  const rad = Math.max(cssW, cssH) * 0.7;
  const cx = worldX + worldW * 0.5;
  const cy = worldY + worldH * 0.5;
  const sep = ctx.createRadialGradient(cx, cy, Math.min(worldW, worldH) * 0.5, cx, cy, rad);
  sep.addColorStop(0, "rgba(5, 10, 15, 0)");
  sep.addColorStop(0.5, "rgba(5, 10, 15, 0.55)");
  sep.addColorStop(1, "rgba(5, 10, 15, 0.85)");
  ctx.fillStyle = sep;
  ctx.fillRect(0, 0, cssW, cssH);
  ctx.restore();
}


export function render(input: RenderInput): void {
  const { ctx, cssWidth, cssHeight, dpr, camera, parcels } = input;
  // PERF — while the camera is moving, skip the costly decorative shadowBlur
  // glows (perimeter bloom, Grid-Zero halo, Reserve dots). Restored when idle.
  const fast = input.quality === "fast";

  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

  // ─── 1. Transparent canvas ─────────────────────────────────────
  // WO-VRD-20 (Claudio) — the grid canvas is now TRANSPARENT and full-
  // bleed so the section's circuit substrate (SUBSTRATE_SVG) shows
  // uniformly across the WHOLE of page 3, under and around the grid, and
  // the grid can pan/zoom over the entire surface. Clear to transparent
  // each frame (degraded mode keeps a faint tint via the DOM layer).
  ctx.clearRect(0, 0, cssWidth, cssHeight);

  // ─── 1b. Tech substrate — DISABLED (WO-VRD-20). The renderer no longer
  // paints its own circuit substrate; the atmospheric substrate is now the
  // section's SUBSTRATE_SVG behind the transparent canvas, so it reads as
  // one continuous plate across the full page. (paintTechSubstrate kept for
  // reference / potential re-enable.)

  // ─── 2. World surface — an actual material with depth ──────────
  const p0 = worldToScreen(camera, 0, 0);
  const p1 = worldToScreen(camera, 1000, 1000);
  const worldW = p1.x - p0.x;
  const worldH = p1.y - p0.y;

  // WO-P5 iter4 §FLOOR-SHADOW-REMOVED — the previous horizontal
  // shadow drop under the world (WO#5 v3.2 §FLOOR-SHADOW) is
  // removed per Claudio review. The Grid sits directly on the
  // substrate: no floor-sheen, no shadow-drop.

  // ═══ COSMIC VISUAL SKIN — transparent surround ═══
  // The canvas stays TRANSPARENT outside the grid plane so the page's cosmic
  // backdrop image (DOM, behind the canvas — see NarrativePage GridSection)
  // shows through in the surrounding space. Only the grid plane below paints
  // pixels; the four-quadrant nebula lives inside the plane. Canonical
  // geometry / hit-test unchanged.

  // WO-VRD-3 §HOLO-SLAB (experimental/visual-rnd — Claudio full-aesthetic
  // rebuild of the 1000×1000 grid). The world is repainted from scratch
  // as a ROUNDED holographic panel. `worldR` is the shared corner radius
  // reused by every interior layer (starfield clip, vignette, frame,
  // outer bloom) so the whole slab reads with soft rounded corners.
  // Canonical geometry (the p0..p1 rect) and the hit-test are UNCHANGED —
  // only the painted pixels are rounded.
  // Grid plane — rounded corners (like the prior holographic slab), while
  // the right-angle corner-bracket segments at the extremes stay sharp
  // (drawn later in the perimeter pass).
  const worldR = Math.max(6, Math.min(72, Math.min(worldW, worldH) * 0.055));
  {
    // 3 PERIMETER BANDS — same rounded-rect silhouette as the frame, stacked
    // concentrically OUTWARD behind the plane, sharing the grid perimeter's
    // CYAN gradient (brighter near the edge, fading outward). Slightly wider
    // than before → layered depth cohesive with the perimeter glow.
    // WO-CONTEST §BAND-FADE (Claudio) — these are the "3 shadow" the user
    // sees vanish instantly on pan. They no longer hard-skip on `fast`;
    // instead their alpha + blur scale with `glowIntensity` (animated by the
    // shell) so they fade OUT smoothly while the grid moves and fade back IN
    // when it settles. Skipped entirely once fully faded (perf parity w/ fast).
    const bandGlow = input.glowIntensity ?? 1;
    if (worldW > 40 && worldH > 40 && bandGlow > 0.01) {
      ctx.save();
      ctx.globalCompositeOperation = "lighter";
      ctx.shadowColor = "rgba(58, 190, 232, 0.4)";
      for (let i = 3; i >= 1; i--) {
        const off = i * 12;                                          // 36 / 24 / 12 px out (wider)
        ctx.shadowBlur = (6.4 + i * 4) * bandGlow;                   // glow −20% (Claudio) · faded
        ctx.strokeStyle = `rgba(58, 190, 232, ${((0.15 - (i - 1) * 0.042) * 0.8) * bandGlow})`; // glow −20% · faded
        ctx.lineWidth = 13;
        strokeRoundedRect(ctx, p0.x - off, p0.y - off, worldW + off * 2, worldH + off * 2, worldR + off);
      }
      ctx.restore();
    }
    // WO-VRD (Claudio) — the grid interior background is RESTORED but kept
    // BARELY PERCEPTIBLE (~10% opacity): the deep-navy plate + nebula only
    // whisper over the page backdrop so the plane reads a touch denser than the
    // surround without hiding it. Grid lines / zones / parcels paint on top.
    const BG_FAINT = 0.30;
    const plate = ctx.createLinearGradient(p0.x, p0.y, p0.x, p1.y);
    plate.addColorStop(0, "#040914");
    plate.addColorStop(1, "#02040b");
    ctx.save();
    ctx.globalAlpha = BG_FAINT;
    ctx.fillStyle = plate;
    fillRoundedRect(ctx, p0.x, p0.y, worldW, worldH, worldR);
    ctx.restore();
    if (worldW > 4 && worldH > 4) {
      const neb = getNebulaImage();
      const s = camera.scale;
      const nebA = (s <= 1.6 ? 0.95 : s >= 6 ? 0.28 : 0.95 - ((s - 1.6) / 4.4) * 0.67) * 0.75;
      ctx.save();
      pathRoundedRect(ctx, p0.x, p0.y, worldW, worldH, worldR);
      ctx.clip();
      ctx.globalAlpha = BG_FAINT;
      if (neb) {
        ctx.globalAlpha = nebA * BG_FAINT;
        ctx.drawImage(neb, p0.x, p0.y, worldW, worldH);
      } else {
        drawNebulaFallback(ctx, p0.x, p0.y, worldW, worldH);
      }
      ctx.restore();
    }
  }

  // WO-P5 iter2 §STARFIELD-PAINT — paint the cached dense starfield
  // tile as a pattern anchored to WORLD coordinates. The pattern
  // transform translates the tile origin so it sits at the world
  // origin in screen space, then scales so 1 tile-pixel = 1 CSS
  // pixel at the current camera zoom (i.e. one tile spans
  // STAR_TILE_CSS world units × camera.scale on screen). This
  // makes the starfield pan/zoom coherently with the territory
  // while remaining an O(1) blit thanks to the cached texture.
  // (starfield is now painted across the whole viewer above — see the
  // COSMIC VISUAL SKIN base + stars pass.)

  // WO-VRD-3 §OUTER-BLOOM — the old squared perimeter (thin cyan frame +
  // inset guide + corner L-brackets + edge ticks) is DELETED. In its
  // place a soft ROUNDED halo bleeds beyond the slab so the panel reads
  // as a hero object emitting into the dark. Additive, constant-cost,
  // purely decorative. The crisp rounded edge itself is drawn last.
  // (outer bloom removed — the crisp luminous perimeter + corner brackets
  // are drawn last, over the grid, in the COSMIC SKIN perimeter pass.)

  // WO#5 v2 §TERRITORY — atmospheric halo around the world edge so
  // the Grid stops feeling like a small square in a black sea.
  // A soft teal glow bleeds beyond the world rectangle, giving the
  // canvas continuity with the section's ambient backdrop.
  //
  // NOTE: this is GENERIC ambient depth. It is NOT a Grid Zero
  // marker. The Grid Zero semantic highlight is derived from the
  // canonical `input.zoneOverlay.manifest` regions further down —
  // if the manifest / sale authority is unavailable, the Grid Zero
  // highlight fails safe (disappears). Presentation may derive
  // from canonical truth; canonical truth may never derive from
  // presentation.
  // (ambient edge halo removed — the four-quadrant nebula raster now
  // carries the atmosphere; the perimeter carries the boundary light.)

  // Central-emphasis vignette that fades the world edges into the
  // shell (cinematic depth). Cooler than v1 (removes warm cast).
  // (edge vignette removed — the nebula ambiences reach the grid border,
  // matching the reference where all four quadrants stay luminous.)

  // ─── 3. Macro grid — 100-cell divisions (subtle structural rhythm) ──
  // WO#5 pass: tone the macro rhythm down. It should suggest a scaled
  // territory, never dominate as "graph paper".
  // WO-P5 iter2 recal: the 1:1 crop shows macro-grid lines as
  // almost subliminal — visible but never protagonists. Alpha
  // pulled back to 0.06 (from the iter1 0.11 experiment) so the
  // corridors' + nucleus' luminance dominance is preserved.
  if (camera.scale > 0.12 && worldW > 4 && worldH > 4) {
    const alpha = Math.min(1, (camera.scale - 0.12) * 3);
    ctx.save();
    pathRoundedRect(ctx, p0.x, p0.y, worldW, worldH, worldR);
    ctx.clip();
    // (a) fine minor lattice — thin low-opacity cyan, BOTH axes, every 50
    //     world units. Reads as a crisp sci-fi mesh over the nebula.
    ctx.strokeStyle = `rgba(74, 194, 224, ${0.0165 * alpha})`;
    ctx.lineWidth = 1;
    for (let g = 50; g < 1000; g += 50) {
      if (g % 250 === 0) continue;
      const sx = Math.round(worldToScreen(camera, g, 0).x) + 0.5;
      ctx.beginPath(); ctx.moveTo(sx, p0.y); ctx.lineTo(sx, p1.y); ctx.stroke();
      const sy = Math.round(worldToScreen(camera, 0, g).y) + 0.5;
      ctx.beginPath(); ctx.moveTo(p0.x, sy); ctx.lineTo(p1.x, sy); ctx.stroke();
    }
    // (b) medium separators — every 250 world units, brighter (skip 500 —
    //     that is the luminous central cross, drawn later).
    ctx.strokeStyle = `rgba(96, 214, 238, ${0.03 * alpha})`;
    ctx.lineWidth = 1;
    for (let g = 250; g < 1000; g += 250) {
      if (g === 500) continue;
      const sx = Math.round(worldToScreen(camera, g, 0).x) + 0.5;
      ctx.beginPath(); ctx.moveTo(sx, p0.y); ctx.lineTo(sx, p1.y); ctx.stroke();
      const sy = Math.round(worldToScreen(camera, 0, g).y) + 0.5;
      ctx.beginPath(); ctx.moveTo(p0.x, sy); ctx.lineTo(p1.x, sy); ctx.stroke();
    }
    ctx.restore();
  }

  // ─── 4. Fine cell lattice at close zoom (10-cell divisions) ─────
  if (camera.scale >= 1.5) {
    const alpha = Math.min(1, (camera.scale - 1.5) * 0.4);
    ctx.save();
    pathRoundedRect(ctx, p0.x, p0.y, worldW, worldH, worldR);
    ctx.clip();
    ctx.strokeStyle = `rgba(90, 190, 215, ${0.009 * alpha})`;
    ctx.lineWidth = 1;
    const w0 = Math.max(0, Math.floor(camera.x / 10) * 10);
    const w1 = Math.min(1000, Math.ceil((camera.x + cssWidth / camera.scale) / 10) * 10);
    const h0 = Math.max(0, Math.floor(camera.y / 10) * 10);
    const h1 = Math.min(1000, Math.ceil((camera.y + cssHeight / camera.scale) / 10) * 10);
    for (let g = w0; g <= w1; g += 10) {
      if (g % 50 === 0) continue;
      const sx = Math.round(worldToScreen(camera, g, 0).x) + 0.5;
      ctx.beginPath(); ctx.moveTo(sx, p0.y); ctx.lineTo(sx, p1.y); ctx.stroke();
    }
    for (let g = h0; g <= h1; g += 10) {
      if (g % 50 === 0) continue;
      const sy = Math.round(worldToScreen(camera, 0, g).y) + 0.5;
      ctx.beginPath(); ctx.moveTo(p0.x, sy); ctx.lineTo(p1.x, sy); ctx.stroke();
    }
    ctx.restore();
  }

  // ─── 5. Canonical zone underlay ───────────────────────────────
  // Rendered AFTER grid so zone borders read against the grid pattern.
  // Regions are disjoint by construction; painter order irrelevant.
  //
  // WO#5 v2b §GRID-ZERO-AUTHORITY — the coral "gravity" glow that
  // signals Grid Zero is derived HERE, from the same canonical
  // manifest regions that paint the LOCKED_GRID_ZERO fills. There
  // is no separately-hardcoded Grid Zero anchor anywhere else in
  // the renderer. If `input.zoneOverlay` is null (sale authority
  // unavailable / stale), the Grid Zero highlight FAILS SAFE — the
  // territory stays visible with only the generic ambient halo.
  // Presentation derives from canonical truth. Canonical truth
  // never derives from presentation.
  if (input.zoneOverlay) {
    // Pre-pass: Grid Zero gravity radials (behind the zone fills so
    // the fill's own inner glow reads on top of the wider halo).
    for (const r of input.zoneOverlay.manifest.regions) {
      if (r.zone !== "LOCKED_GRID_ZERO") continue;
      const a = worldToScreen(camera, r.x, r.y);
      const b = worldToScreen(camera, r.x + r.w, r.y + r.h);
      const w = b.x - a.x;
      const h = b.y - a.y;
      if (w <= 0 || h <= 0) continue;
      if (a.x + w < 0 || a.y + h < 0 || a.x > cssWidth || a.y > cssHeight) continue;
      const gcx = a.x + w * 0.5;
      const gcy = a.y + h * 0.5;
      // WO-VRD-1 §GRID-ZERO-DOMINANCE (experimental/visual-rnd) — the
      // reference reads the nucleus as the single most luminous FOCAL
      // POINT of the entire scene. The canonical Grid Zero region is
      // only 20×20 world cells (~20px at fit), so region-relative bloom
      // radii render near-invisible. We therefore scale the *presentation*
      // bloom off the WORLD size (with a region-relative floor) so the
      // coral heart glows across the central corridor cross exactly like
      // the reference. This is pure additive light centred on the
      // canonical LOCKED_GRID_ZERO centroid — geometry / authority are
      // unchanged, and the cyan corridors + VIP plates paint AFTER this
      // pass so they retain their luminance dominance in the channels.
      const worldMin = Math.min(worldW, worldH);
      const gr = Math.max(w, h);
      // WO-VRD-27 (Claudio) — the coral nucleus used to be a ROUND radial
      // burst with a near-white (0.96) hot core: too bright. It is now a
      // DRASTICALLY dimmer ROUNDED-SQUARE glow — a soft blurred rounded
      // square halo + a dim rounded-square core. Additive light centred on
      // the canonical LOCKED_GRID_ZERO centroid; geometry/authority intact.
      ctx.save();
      ctx.globalCompositeOperation = "lighter";
      // (a) Soft outer halo — rounded SQUARE via shadowBlur (no radial).
      const glowHalf = Math.max(gr * 0.95, worldMin * 0.085);
      const glowRad = Math.max(4, glowHalf * 0.38);
      ctx.shadowColor = "rgba(210, 40, 70, 0.272)";                 // glow −20% (Claudio)
      ctx.shadowBlur = fast ? 0 : Math.min(24, Math.max(8, gr * 1.0));         // glow −20%
      ctx.fillStyle = "rgba(196, 32, 62, 0.112)";                  // glow −20%
      fillRoundedRect(ctx, gcx - glowHalf, gcy - glowHalf, glowHalf * 2, glowHalf * 2, glowRad);
      ctx.shadowBlur = 0;
      // (b) Dim rounded-square core (rounded corners, low luminance).
      const coreHalf = Math.max(4, glowHalf * 0.46);
      const coreRad = Math.max(3, coreHalf * 0.42);
      ctx.fillStyle = "rgba(236, 84, 116, 0.24)";                  // glow −20%
      fillRoundedRect(ctx, gcx - coreHalf, gcy - coreHalf, coreHalf * 2, coreHalf * 2, coreRad);
      ctx.restore();

      // WO-P5 iter2 §GRID-ZERO-FRAMED — the reference crop reads
      // the nucleus as a small coral core mounted on a layered
      // holographic plaque: a translucent tile a bit lighter than
      // the world fill, framed by a thin luminous outline.
      // WO-P5 iter3 upgrade: frames get a mild additive glow so
      // they read as ENERGIZED plaques, not CAD strokes.
      // Outer plaque (translucent lift, ~1.6× the region size).
      // WO-VRD-28 (Claudio) — the plaque + its frames were drawn with SQUARE
      // (right-angle) corners and clashed with the rounded language of the
      // rest of the map. They are now ROUNDED rectangles.
      const plaqueR = Math.max(w, h) * 0.82;
      const plaqueRad = Math.max(4, plaqueR * 0.30);
      ctx.fillStyle = "rgba(140, 200, 220, 0.08)";
      fillRoundedRect(ctx, gcx - plaqueR, gcy - plaqueR, plaqueR * 2, plaqueR * 2, plaqueRad);
      // Outer + inner frames painted with additive compositing so
      // they read as luminous edges rather than dead strokes.
      ctx.save();
      ctx.globalCompositeOperation = "lighter";
      ctx.strokeStyle = "rgba(220, 235, 245, 0.45)";
      ctx.lineWidth = 1;
      strokeRoundedRect(
        ctx,
        Math.round(gcx - plaqueR) + 0.5,
        Math.round(gcy - plaqueR) + 0.5,
        Math.round(plaqueR * 2),
        Math.round(plaqueR * 2),
        plaqueRad,
      );
      const innerFrame = Math.max(w, h) * 0.62;
      const innerRad = Math.max(3, innerFrame * 0.32);
      ctx.strokeStyle = "rgba(240, 250, 255, 0.75)";
      ctx.lineWidth = 1;
      strokeRoundedRect(
        ctx,
        Math.round(gcx - innerFrame) + 0.5,
        Math.round(gcy - innerFrame) + 0.5,
        Math.round(innerFrame * 2),
        Math.round(innerFrame * 2),
        innerRad,
      );
      ctx.restore();
    }

    // WO-VRD-11 §CORRIDOR-JUNCTIONS (Claudio) — the PREMIUM corridors are
    // canonically DISJOINT rectangles that ABUT edge-to-edge. PREMIUM bodies
    // now fill with SQUARE corners (see `zr` below) so adjacent corridors
    // touch seamlessly — the road body is one continuous shape with no
    // notch/gap and no perimeter markings (rails / halo strokes removed per
    // Claudio). Presentation only; geometry / hit-test untouched.

    // First pass: fills
    for (const r of input.zoneOverlay.manifest.regions) {
      const a = worldToScreen(camera, r.x, r.y);
      const b = worldToScreen(camera, r.x + r.w, r.y + r.h);
      const w = b.x - a.x;
      const h = b.y - a.y;
      if (w <= 0 || h <= 0) continue;
      if (a.x + w < 0 || a.y + h < 0 || a.x > cssWidth || a.y > cssHeight) continue;
      const style = ZONE_STYLE[r.zone as ZoneRole];
      if (!style) continue;
      // WO-VRD-23 (Claudio) — STANDARD zone plates removed: they carved
      // the grid into rounded "slots" (partitioned look). Skipping them
      // keeps the field clean/infinite. Corridors (PREMIUM), VIP, Grid
      // Zero and Reserve landmarks still render. Canonical zone data /
      // hit-test / pricing are untouched — this is presentation only.
      if (r.zone === "STANDARD") continue;

      // Fill (WO-VRD-3 — zone plates rounded; WO-VRD-9 — PREMIUM corridors
      // square so abutting road segments merge into one continuous body).
      const zr = r.zone === "PREMIUM" ? 0 : Math.max(2, Math.min(w, h) * 0.16);
      // WO-VRD-26 (Claudio) — +30% transparency across the grid drawing
      // zones: plate fills paint at 0.70× their nominal alpha.
      // Reserve (Claudio) — the plate rectangle itself is removed: only the
      // "RESERVE" label + the four red corner dots remain (drawn below).
      if (r.zone !== "LOCKED_RESERVE") {
        ctx.save();
        // Fade the teal boulevards/plates a further 25% (Claudio) — the red
        // Grid-Zero core keeps its nominal alpha.
        ctx.globalAlpha = r.zone === "LOCKED_GRID_ZERO" ? 0.7 : 0.018; // boulevards + big central square fill → ~10% (Claudio Jun26)
        ctx.fillStyle = style.fill;
        fillRoundedRect(ctx, a.x, a.y, w, h, zr);
        ctx.restore();
      }

      // WO-VRD-2 §PLATE-GLASS (experimental/visual-rnd) — Premium/VIP
      // plates gain an internal top-lit glass body so they read as
      // illuminated technological modules, not empty wireframe boxes.
      // Presentation only, source-over, restrained; canonical zone
      // geometry unchanged.
      if (r.zone === "PREMIUM" && w > 16 && h > 16) {
        ctx.save();
        ctx.globalAlpha = 0.026; // teal boulevard glass sheen → ~10% (Claudio Jun26)
        const isV = false;
        const pg = ctx.createLinearGradient(a.x, a.y, a.x, a.y + h);
        pg.addColorStop(0,    isV ? "rgba(60, 165, 168, 0.12)" : "rgba(44, 140, 145, 0.09)");
        pg.addColorStop(0.45, "rgba(8, 30, 38, 0)");
        pg.addColorStop(1,    isV ? "rgba(28, 120, 128, 0.08)" : "rgba(18, 95, 102, 0.06)");
        ctx.fillStyle = pg;
        fillRoundedRect(ctx, a.x, a.y, w, h, zr);
        ctx.restore();
      }

      // Reserve: WO#5 v3 — no more diagonal hatch. Clean holographic
      // corner brackets + optional "RESERVE" label. Reads as a
      // controlled special territory, not a disabled UI box.
      // WO-P5 iter2 calibration: (a) label gate lowered so the
      // "RESERVE" label is visible at fit-zoom (matches the 1:1
      // crop); (b) small coral corner accents added — the crop
      // shows distinctive pink/coral marks at every Reserve
      // corner, a signature detail we now honour.
      // WO (Claudio) — a RESERVE landmark must NOT paint over land that is
      // already CLAIMED. When an owned parcel covers the reserve region the
      // owner reads the "◇ RESERVE" glyph + red corner dots as a stray
      // "diamond" sitting on their own land. Suppress the marker there.
      // Presentation only; the canonical LOCKED_RESERVE zone data, pricing
      // and hit-test are untouched.
      const reserveCx = r.x + r.w / 2;
      const reserveCy = r.y + r.h / 2;
      const reserveClaimed = parcels.some(
        (p) => reserveCx >= p.x && reserveCx < p.x + p.w && reserveCy >= p.y && reserveCy < p.y + p.h,
      );
      if (r.zone === "LOCKED_RESERVE" && w > 24 && h > 24 && !reserveClaimed) {
        const cs = [
          [a.x, a.y, 1, 1],
          [b.x, a.y, -1, 1],
          [a.x, b.y, 1, -1],
          [b.x, b.y, -1, -1],
        ] as const;
        // Reserve (Claudio) — the plate rectangle is removed; only these
        // four RED corner dots (at the parcel's extremities) + the RESERVE
        // label remain. Dots sit right at the corners and read clearly red.
        if (Math.min(w, h) >= 40) {
          // Reserve corner dots: size −40% + glow −50% (Claudio).
          const accR = Math.max(1.2, Math.min(2.04, Math.min(w, h) * 0.0168));
          const accOffset = accR + 1; // hug the very corner
          // Quadrant-tinted RESERVE corner dots (always on) — bright, so the
          // reserve landmarks read clearly over the nebula/backdrop. Colour by
          // the quadrant the region centre falls in (UL blue, UR violet,
          // LL amber, LR emerald), matching the four nebula ambiences.
          const dotColor = quadrantDotColor(reserveCx, reserveCy);
          ctx.save();
          ctx.globalCompositeOperation = "lighter";
          ctx.shadowColor = dotColor;
          ctx.shadowBlur = fast ? 0 : 3.5;
          ctx.fillStyle = dotColor;
          for (const [x, y, dx, dy] of cs) {
            ctx.beginPath();
            ctx.arc(x + dx * accOffset, y + dy * accOffset, accR, 0, Math.PI * 2);
            ctx.fill();
          }
          ctx.restore();
        }
        // Label at fit-zoom onwards — WO-P5 iter2 lowered the gate
        // from 90 px to 60 px so the "◇ RESERVE" mark reads at fit
        // on 1000×1000 territories as it does in the reference.
        if (Math.min(w, h) >= 60) {
          const cxL = a.x + w * 0.5;
          const cyL = a.y + h * 0.5;
          ctx.fillStyle = "rgba(150, 210, 240, 0.85)";
          const fontPx = Math.max(8, Math.min(12, Math.min(w, h) * 0.11));
          ctx.font = `600 ${fontPx}px "Chakra Petch", ui-sans-serif`;
          ctx.textAlign = "center";
          ctx.textBaseline = "middle";
          // The lozenge glyph ◇ precedes the label like in the crop.
          ctx.fillText("◇ RESERVE", cxL, cyL);
          ctx.textAlign = "start";
          ctx.textBaseline = "alphabetic";
        }
      }

      // Inner glow band (Premium/VIP/Grid Zero identity)
      if (style.inner && w > 20 && h > 20 && r.zone !== "LOCKED_RESERVE") {
        ctx.save();
        ctx.globalAlpha = r.zone === "LOCKED_GRID_ZERO" ? 0.8 : 0.028; // boulevards + big central square inner glow → ~10% (Claudio Jun26)
        const inset = Math.min(6, Math.max(2, camera.scale * 0.6));
        const iw = w - inset * 2;
        const ih = h - inset * 2;
        const grad = ctx.createLinearGradient(a.x, a.y, a.x, a.y + h);
        grad.addColorStop(0, style.inner);
        grad.addColorStop(0.5, "rgba(0,0,0,0)");
        grad.addColorStop(1, style.inner);
        ctx.fillStyle = grad;
        // WO-VRD-29 (Claudio) — the inner glow band was a SHARP fillRect and
        // was what made the central VIP-core "big square" read with hard 90°
        // corners. It now follows the plate's rounded corners.
        const ir = Math.max(2, Math.min(iw, ih) * 0.16);
        fillRoundedRect(ctx, a.x + inset, a.y + inset, iw, ih, ir);
        ctx.restore();
      }
    }

    // Second pass: borders (drawn on top so they land crisply)
    ctx.lineWidth = 1;
    for (const r of input.zoneOverlay.manifest.regions) {
      const a = worldToScreen(camera, r.x, r.y);
      const b = worldToScreen(camera, r.x + r.w, r.y + r.h);
      const w = b.x - a.x;
      const h = b.y - a.y;
      if (w <= 0 || h <= 0) continue;
      if (a.x + w < 0 || a.y + h < 0 || a.x > cssWidth || a.y > cssHeight) continue;
      const style = ZONE_STYLE[r.zone as ZoneRole];
      if (!style) continue;
      if (r.zone === "STANDARD") continue; // WO-VRD-23 — no slot outlines
      ctx.strokeStyle = style.border;
      ctx.lineWidth = r.zone === "LOCKED_GRID_ZERO" ? 1.5 : 1;
      if (r.zone === "PREMIUM" || r.zone === "VIP_CORE_SELLABLE") {
        // WO-VRD-11 (Claudio) — no perimeter marking on the roads
        // ("strade") nor on the central squares (VIP core). Fill /
        // inner-glow only; the crisp rail/outline is dropped.
      } else if (r.zone === "LOCKED_RESERVE") {
        // Reserve (Claudio) — no plate outline: the rectangle is gone,
        // only the "RESERVE" label + the four red corner dots remain.
      } else {
        strokeRoundedRect(
          ctx,
          Math.round(a.x) + 0.5,
          Math.round(a.y) + 0.5,
          Math.round(w) - 1,
          Math.round(h) - 1,
          Math.max(2, Math.min(w, h) * 0.16),
        );
      }

      // Grid Zero: WO#5 v3 — nucleus rings (holographic layered
      // treatment). Coral concentric rings inside the region, drawn
      // AFTER the border so they read on top of the fill/border.
      if (r.zone === "LOCKED_GRID_ZERO" && w > 40 && h > 40) {
        // 1) precision corner ticks (existing)
        const tick = Math.min(12, Math.min(w, h) * 0.12);
        ctx.strokeStyle = "rgba(255, 130, 140, 0.95)";
        ctx.lineWidth = 1.8;
        const corners = [
          [a.x, a.y, 1, 1],
          [b.x, a.y, -1, 1],
          [a.x, b.y, 1, -1],
          [b.x, b.y, -1, -1],
        ] as const;
        for (const [x, y, dx, dy] of corners) {
          ctx.beginPath();
          ctx.moveTo(x, y + dy * tick);
          ctx.lineTo(x, y);
          ctx.lineTo(x + dx * tick, y);
          ctx.stroke();
        }
        // 2) Concentric coral rings (holographic nucleus — circular)
        const nx = a.x + w * 0.5;
        const ny = a.y + h * 0.5;
        const rings = [0.72, 0.5, 0.32];
        for (let i = 0; i < rings.length; i++) {
          const rr = Math.min(w, h) * rings[i] * 0.5;
          ctx.strokeStyle = `rgba(216, ${24 + i * 18}, ${62 + i * 14}, ${0.85 - i * 0.15})`;
          ctx.lineWidth = 1.5;
          ctx.beginPath();
          ctx.arc(nx, ny, rr, 0, Math.PI * 2);
          ctx.stroke();
        }
        // 3) Radial coral core (circular hot centre)
        const core = Math.min(w, h) * 0.16;
        const coreGrad = ctx.createRadialGradient(nx, ny, 0, nx, ny, core * 1.4);
        coreGrad.addColorStop(0,   "rgba(232, 40, 84, 1)");
        coreGrad.addColorStop(0.6, "rgba(200, 12, 56, 0.78)");
        coreGrad.addColorStop(1,   "rgba(180, 12, 52, 0)");
        ctx.fillStyle = coreGrad;
        ctx.beginPath();
        ctx.arc(nx, ny, core * 1.4, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = "#C41442";
        ctx.beginPath();
        ctx.arc(nx, ny, core * 0.5, 0, Math.PI * 2);
        ctx.fill();
        // WO-VRD-1 — white-hot pinpoint centre (the reference's single
        // brightest pixel). Additive so it reads as emitted light.
        ctx.save();
        ctx.globalCompositeOperation = "lighter";
        ctx.fillStyle = "rgba(255, 210, 224, 0.92)";
        const hot = Math.max(1.5, core * 0.55);
        ctx.beginPath();
        ctx.arc(nx, ny, hot * 0.5, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
        // 4) Satellite dots — four small coral pulses (circular).
        if (Math.min(w, h) >= 60) {
          const sat = Math.max(2, Math.min(w, h) * 0.02);
          const off = Math.min(w, h) * 0.36;
          const positions: Array<[number, number]> = [
            [nx, ny - off],
            [nx + off, ny],
            [nx, ny + off],
            [nx - off, ny],
          ];
          ctx.fillStyle = "rgba(216, 44, 84, 0.95)";
          for (const [sx, sy] of positions) {
            ctx.beginPath();
            ctx.arc(sx, sy, sat, 0, Math.PI * 2);
            ctx.fill();
          }
        }
      }
    }

    // WO-VRD-29 (Claudio) — the central "big square" (VIP-core plate) hard
    // corners came from the inner-glow band; it now renders rounded. See the
    // "Inner glow band" pass above.


    // WO-VRD-11 §PREMIUM-CHANNEL — the corridors read as soft illuminated
    // channels: additive interior gradient only. The former outlined
    // "energy bar" strokes (rails) were removed per Claudio.
    ctx.save();
    ctx.globalCompositeOperation = "lighter";
    for (const r of input.zoneOverlay.manifest.regions) {
      if (r.zone !== "PREMIUM") continue;
      const a = worldToScreen(camera, r.x, r.y);
      const b = worldToScreen(camera, r.x + r.w, r.y + r.h);
      const w = b.x - a.x;
      const h = b.y - a.y;
      if (w <= 6 || h <= 6) continue;
      if (a.x + w < 0 || a.y + h < 0 || a.x > cssWidth || a.y > cssHeight) continue;
      const isVip = false;
      const rx = Math.round(a.x) + 0.5;
      const ry = Math.round(a.y) + 0.5;
      const rw = Math.round(w) - 1;
      const rh = Math.round(h) - 1;
      const pr = 0;
      // WO-VRD-11 (Claudio) — perimeter halo STROKES removed (no rail /
      // outline markings on the roads). Only the soft interior gradient
      // fill remains so the corridor still reads as an illuminated channel.
      const grad = w > h
        ? ctx.createLinearGradient(rx, ry, rx, ry + rh)
        : ctx.createLinearGradient(rx, ry, rx + rw, ry);
      const gStrong = isVip ? "rgba(70, 175, 178, 0.0098)" : "rgba(44, 150, 152, 0.007)"; // boulevard channel → ~10% (Claudio Jun26)
      const gWeak =   "rgba(0, 0, 0, 0)";
      grad.addColorStop(0,    gStrong);
      grad.addColorStop(0.5,  gWeak);
      grad.addColorStop(1,    gStrong);
      ctx.fillStyle = grad;
      fillRoundedRect(ctx, rx, ry, rw, rh, pr);
    }
    ctx.restore();

    // WO-VRD-11 §DE-MARK (Claudio) — the corridor SEGMENTATION pass
    // (internal dividers + per-segment "module" squares + light-node
    // dots) has been REMOVED: the reference no longer wants the central
    // squares / perimeter tick markings inside the roads. The corridors
    // now read as clean illuminated channels (fill + soft interior
    // gradient only). Canonical geometry / hit-test untouched.


    // WO-P5 iter2 — Grid Zero rays REMOVED. The 1:1 reference crop
    // (`assets/mock/claudio_reference_v3.jpeg` grid area) shows no
    // diagonal ray beams crossing the world. Presentation now stops
    // at the concentric gravity halo pass above; the corridors and
    // nucleus carry the "light distribution" reading on their own.
    // (The prior v3.2 ray block was gated to the same canonical
    // LOCKED_GRID_ZERO region — nothing else derived from it.)
  }

  // WO-VRD-33 §REVEAL-AREAS — optional fluorescent taxonomy overlay.
  // Painted AFTER the base zone passes and BEFORE parcels so the zone
  // colours pop while individual parcels still read on top. Derives
  // only from the canonical zoneOverlay regions; fails safe when null.
  if (input.revealZones && input.zoneOverlay) {
    const t =
      typeof performance !== "undefined" ? performance.now() : Date.now();
    const pulse = 0.5 + 0.5 * Math.sin(t / 500);
    paintZoneReveal(
      ctx,
      camera,
      input.zoneOverlay.manifest,
      cssWidth,
      cssHeight,
      pulse,
      input.revealHoverRole ?? null,
    );
  }

  // ─── 6. Parcels — WO#5 v3 holographic property treatment ────
  // Presentation is 2.5D: rounded visual corners, edge glow, inner
  // material lift. Canonical geometry (parcel.x, .y, .w, .h) and
  // hit-testing remain untouched — the pointer semantics still
  // operate on the axis-aligned rectangle in world space.
  for (const parcel of parcels) {
    const a = worldToScreen(camera, parcel.x, parcel.y);
    const b = worldToScreen(camera, parcel.x + parcel.w, parcel.y + parcel.h);
    const w = b.x - a.x;
    const h = b.y - a.y;
    if (w <= 0 || h <= 0) continue;
    if (a.x + w < 0 || a.y + h < 0 || a.x > cssWidth || a.y > cssHeight) continue;

    const radius = Math.max(0, Math.min(6, Math.min(w, h) * 0.14));
    const fill = ownerColor(parcel.owner);
    // WO#5 v3b §PARCEL-VISIBILITY — sub-cell parcels (min(w,h) < 6 CSS
    // px, e.g. a 2×2-world parcel at fit-zoom) cannot afford a 1px
    // hairline stroke on top of a fill of comparable size: at
    // lineWidth=1 the stroke paints ~50–65% of the pixels of a 2×2
    // body-fill, visually replacing the owner color with the border
    // teal. `tinyParcel` gates the border + inner-highlight overlays
    // off for those parcels; the owner-color body stays the dominant
    // reading. Geometry / hit-test / world coords are unchanged.
    const tinyParcel = Math.min(w, h) < 6;

    // WO-P5 iter3 §PARCEL-GLASS — the reference reads parcels as
    // TRANSLUCENT ENERGISED GLASS, not opaque UI rectangles.
    // For non-tiny parcels we paint:
    //   (a) additive outer bloom halo (soft, wide)
    //   (b) translucent body fill (alpha ≈ 0.55) so the starfield
    //       is still visible beneath the parcel
    //   (c) top-→bottom lift highlight (glass shine reading)
    //   (d) additive bright inner core line + wider halo stroke
    // Tiny parcels keep their opaque fill + no border — the
    // WO#5 tiny-parcel visibility guard is preserved.
    if (!tinyParcel && w >= 6 && h >= 6) {
      // WO-VRD-25 (Claudio) — the purchased-parcel perimeter used to be
      // THICK and LUMINOUS (outer additive bloom + 2px cyan halo + a
      // near-white 0.90 inner core stroke). Per request it is now
      // "sottilissimo e appena percepibile": the bloom halo and the wide
      // additive edge are gone; only a single faint hairline remains so
      // the glass body stays the dominant reading.
      // (b) Translucent body fill.
      ctx.save();
      ctx.globalAlpha = 0.55;
      ctx.fillStyle = fill;
      fillRoundedRect(ctx, a.x, a.y, w, h, radius);
      ctx.restore();
      // (c) Glass shine (bright top, faint bottom shadow).
      const shine = ctx.createLinearGradient(a.x, a.y, a.x, a.y + h);
      shine.addColorStop(0,    "rgba(255, 255, 255, 0.22)");
      shine.addColorStop(0.4,  "rgba(255, 255, 255, 0.04)");
      shine.addColorStop(0.7,  "rgba(0, 0, 0, 0)");
      shine.addColorStop(1,    "rgba(0, 20, 30, 0.18)");
      ctx.fillStyle = shine;
      fillRoundedRect(ctx, a.x, a.y, w, h, radius);
      // (d) Border: a single barely-perceptible hairline — no bloom, no
      // additive glow, no bright near-white core.
      ctx.strokeStyle = "rgba(180, 225, 240, 0.22)";
      ctx.lineWidth = 0.75;
      strokeRoundedRect(ctx, a.x + 0.5, a.y + 0.5, w - 1, h - 1, radius);
    } else {
      // Tiny parcels — WO#5 visibility guard preserved verbatim:
      // solid owner-colored body, no stroke overlay that would
      // overpaint the identity at 2×2 or 3×3 CSS px.
      ctx.fillStyle = fill;
      fillRoundedRect(ctx, a.x, a.y, w, h, radius);
    }

    // Sub-cell tick — 1px cyan line along the top edge for that
    // "circuit surface" reading (only at meaningful zoom)
    if (camera.scale >= 2 && w >= 12) {
      ctx.strokeStyle = "rgba(200, 245, 255, 0.75)";
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(a.x + radius + 1, a.y + 2.5);
      ctx.lineTo(a.x + w - radius - 1, a.y + 2.5);
      ctx.stroke();
    }
  }

  // Parcel outlines at medium zoom
  if (camera.scale >= 3) {
    ctx.strokeStyle = "rgba(0, 0, 0, 0.5)";
    ctx.lineWidth = 1;
    for (const parcel of parcels) {
      const a = worldToScreen(camera, parcel.x, parcel.y);
      const b = worldToScreen(camera, parcel.x + parcel.w, parcel.y + parcel.h);
      ctx.strokeRect(a.x + 0.5, a.y + 0.5, b.x - a.x - 1, b.y - a.y - 1);
    }
  }

  // ─── 7. Cell grid at close zoom (LOD — almost imperceptible) ────
  if (camera.scale >= 5) {
    const alpha = Math.min(1, (camera.scale - 5) * 0.15);
    ctx.strokeStyle = `rgba(140, 156, 180, ${0.00375 * alpha})`;
    ctx.lineWidth = 1;
    const w0 = Math.max(0, Math.floor(camera.x));
    const w1 = Math.min(1000, Math.ceil(camera.x + cssWidth / camera.scale));
    const h0 = Math.max(0, Math.floor(camera.y));
    const h1 = Math.min(1000, Math.ceil(camera.y + cssHeight / camera.scale));
    for (let gx = w0; gx <= w1; gx++) {
      if (gx % 10 === 0) continue;
      const sx = Math.round(worldToScreen(camera, gx, 0).x) + 0.5;
      ctx.beginPath();
      ctx.moveTo(sx, worldToScreen(camera, gx, h0).y);
      ctx.lineTo(sx, worldToScreen(camera, gx, h1).y);
      ctx.stroke();
    }
    for (let gy = h0; gy <= h1; gy++) {
      if (gy % 10 === 0) continue;
      const sy = Math.round(worldToScreen(camera, 0, gy).y) + 0.5;
      ctx.beginPath();
      ctx.moveTo(worldToScreen(camera, w0, gy).x, sy);
      ctx.lineTo(worldToScreen(camera, w1, gy).x, sy);
      ctx.stroke();
    }
  }

  // ─── 7b. LOD0 Brand Image layer ───────────────────────────────
  // Above parcels + grid, below hover/selection so highlights stay legible.
  if (input.brandImages && input.brandImages.length > 0) {
    drawBrandImages(ctx, camera, input.brandImages);
  }

  // ─── 8. Hover / Selection overlays ────────────────────────────
  const overlay = (parcel: Parcel, style: string, width: number) => {
    const a = worldToScreen(camera, parcel.x, parcel.y);
    const b = worldToScreen(camera, parcel.x + parcel.w, parcel.y + parcel.h);
    const pr = Math.max(2, Math.min((b.x - a.x), (b.y - a.y)) * 0.16);
    ctx.strokeStyle = style;
    ctx.lineWidth = width;
    strokeRoundedRect(ctx, a.x, a.y, b.x - a.x, b.y - a.y, pr);
  };
  const drawCornerBrackets = (
    parcel: Parcel,
    color: string,
    width: number,
    tickFrac = 0.18,
  ) => {
    const a = worldToScreen(camera, parcel.x, parcel.y);
    const b = worldToScreen(camera, parcel.x + parcel.w, parcel.y + parcel.h);
    const w = b.x - a.x;
    const h = b.y - a.y;
    const t = Math.max(4, Math.min(w, h) * tickFrac);
    ctx.strokeStyle = color;
    ctx.lineWidth = width;
    const cs = [
      [a.x, a.y, 1, 1],
      [b.x, a.y, -1, 1],
      [a.x, b.y, 1, -1],
      [b.x, b.y, -1, -1],
    ] as const;
    for (const [x, y, dx, dy] of cs) {
      ctx.beginPath();
      ctx.moveTo(x, y + dy * t);
      ctx.lineTo(x, y);
      ctx.lineTo(x + dx * t, y);
      ctx.stroke();
    }
  };
  const findById = (id: number | null) =>
    id == null ? null : parcels.find((p) => p.id === id) ?? null;
  const hov = findById(input.hoveredId);
  if (hov) overlay(hov, "rgba(45, 182, 240, 0.85)", 1.5);
  const sel = findById(input.selectedId);
  if (sel) {
    // WO-P2 selection — cyan outer halo tightened (6→4) so nearby
    // cells stay legible; coral inner ring uses spec red; corner
    // brackets use spec cyan for a "targeted lock" reading with the
    // dominant precious color, and a secondary spec-coral hairline.
    overlay(sel, "rgba(58, 200, 255, 0.32)", 4);
    overlay(sel, "#FF0044", 1.5);
    drawCornerBrackets(sel, "#3AC8FF", 2, 0.24);
  }

  // ─── 9. Rectangle selection overlay (world-space) ─────────────
  const rect = input.selectionOverlay;
  if (rect) {
    const a = worldToScreen(camera, rect.selection.x, rect.selection.y);
    const b = worldToScreen(
      camera,
      rect.selection.x + rect.selection.w,
      rect.selection.y + rect.selection.h,
    );
    const w = b.x - a.x;
    const h = b.y - a.y;
    // Teal tinted fill (WO#5 v2)
    ctx.fillStyle =
      rect.kind === "draft"
        ? "rgba(45, 182, 240, 0.10)"
        : "rgba(45, 182, 240, 0.16)";
    ctx.fillRect(a.x, a.y, w, h);
    // Outer glow band
    ctx.strokeStyle = "rgba(45, 182, 240, 0.35)";
    ctx.lineWidth = 3;
    ctx.strokeRect(a.x, a.y, w, h);
    // Precision outline
    ctx.strokeStyle = rect.kind === "draft" ? "#74D4FF" : "#2DB6F0";
    ctx.lineWidth = 1.5;
    if (rect.kind === "draft") ctx.setLineDash([6, 3]);
    else ctx.setLineDash([]);
    ctx.strokeRect(a.x + 0.5, a.y + 0.5, w - 1, h - 1);
    ctx.setLineDash([]);
    // Corner registration ticks (coral)
    const tick = 6;
    ctx.strokeStyle = "#FF4757";
    ctx.lineWidth = 1.5;
    const corners = [
      [a.x, a.y, 1, 1],
      [b.x, a.y, -1, 1],
      [a.x, b.y, 1, -1],
      [b.x, b.y, -1, -1],
    ] as const;
    for (const [x, y, dx, dy] of corners) {
      ctx.beginPath();
      ctx.moveTo(x, y + dy * tick);
      ctx.lineTo(x, y);
      ctx.lineTo(x + dx * tick, y);
      ctx.stroke();
    }
  }

  // ─── 10. World boundary — WO-VRD-22 (Claudio) ────────────────────
  // Reduced to a BARELY-PERCEPTIBLE rounded edge: no bright neon cadre,
  // no thick luminous corner arcs. Just enough to define where the plate
  // ends. Geometry unchanged (still the p0..p1 rect).
  // ─── 10. COSMIC SKIN — outer perimeter · central axes · nexus ────
  // Drawn LAST so they read crisply above the nebula, grid and parcels.
  // All flat; the cross and nexus are locked to the AUTHORITATIVE world
  // centre (500,500). Presentation only — geometry / hit-test untouched.
  if (worldW > 20 && worldH > 20) {
    drawCentralAxes(ctx, camera);
    drawGridPerimeter(ctx, p0.x, p0.y, worldW, worldH, worldR, fast);
    // WO-VRD-33 (Claudio) — the Grid Zero core region is a tiny 20×20-unit
    // square fully covered by the nexus body + logo, so the reveal fill can
    // never surface it. When "Grid Zero" is the hovered legend category we
    // instead light the NEXUS itself (glow + border + logo) in red so the
    // locked nucleus is clearly identifiable.
    const revealGridZero =
      !!input.revealZones && input.revealHoverRole === "LOCKED_GRID_ZERO";
    const nexusPulse = revealGridZero
      ? 0.5 + 0.5 * Math.sin(
          (typeof performance !== "undefined" ? performance.now() : Date.now()) / 500,
        )
      : 0;
    drawCentralNexus(ctx, camera, worldW, worldH, revealGridZero, nexusPulse);
  }
}

/* ─── Rounded-rect canvas helpers ─────────────────────────────
 * Pure presentation. The canonical parcel geometry (world x/y/w/h)
 * remains rectangular; only the pixel painting is rounded. Camera
 * math, hit-testing, drag selection, quote/purchase coordinates
 * remain untouched. If `Path2D.roundRect` is available we use it
 * (modern browsers); otherwise we fall back to a manual polyline
 * that produces the same visual footprint. */
function pathRoundedRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  const rr = Math.max(0, Math.min(r, Math.min(w, h) * 0.5));
  ctx.beginPath();
  // Feature-detect `roundRect` on the runtime context.
  const rr2 = (ctx as unknown as {
    roundRect?: (x: number, y: number, w: number, h: number, r: number) => void;
  }).roundRect;
  if (typeof rr2 === "function") {
    rr2.call(ctx, x, y, w, h, rr);
    return;
  }
  ctx.moveTo(x + rr, y);
  ctx.lineTo(x + w - rr, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + rr);
  ctx.lineTo(x + w, y + h - rr);
  ctx.quadraticCurveTo(x + w, y + h, x + w - rr, y + h);
  ctx.lineTo(x + rr, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - rr);
  ctx.lineTo(x, y + rr);
  ctx.quadraticCurveTo(x, y, x + rr, y);
  ctx.closePath();
}
function fillRoundedRect(
  ctx: CanvasRenderingContext2D,
  x: number, y: number, w: number, h: number, r: number,
): void { pathRoundedRect(ctx, x, y, w, h, r); ctx.fill(); }
function strokeRoundedRect(
  ctx: CanvasRenderingContext2D,
  x: number, y: number, w: number, h: number, r: number,
): void { pathRoundedRect(ctx, x, y, w, h, r); ctx.stroke(); }

/* ═══ COSMIC VISUAL SKIN (Checkpoint 1) — environment + structure ═══
 * Presentation only. The nebula raster is a world-anchored ENVIRONMENT
 * asset (no grid/parcels baked in); the perimeter, central cross and
 * nexus are painted procedurally and locked to the authoritative world
 * centre (500,500). Camera math, hit-testing and geometry are untouched. */

// Nebula environment raster (lazy-loaded, cached). A repaint hook lets
// the viewer request one repaint once the image has decoded.
let _nebImg: HTMLImageElement | null = null;
let _nebReady = false;
let _nebRequested = false;
let _repaintHook: (() => void) | null = null;

/** Register a one-shot repaint callback (the viewer passes its markDirty). */
export function setRepaintHook(fn: (() => void) | null): void {
  _repaintHook = fn;
}

function getNebulaImage(): HTMLImageElement | null {
  if (_nebReady && _nebImg) return _nebImg;
  if (_nebRequested || typeof document === "undefined") return null;
  _nebRequested = true;
  const img = new Image();
  img.decoding = "async";
  img.onload = () => {
    _nebImg = img;
    _nebReady = true;
    _repaintHook?.();
  };
  img.onerror = () => {
    // Allow a later retry; the procedural fallback carries the ambience.
    _nebRequested = false;
  };
  img.src = "/cosmic/nebula-plate.jpg";
  return null;
}

// Official THE GRID icon logo (icon-only, transparent) for the central
// nexus — lazy-loaded, cached, mirrors the nebula loader.
let _logoImg: HTMLImageElement | null = null;
let _logoReady = false;
let _logoRequested = false;
function getGridLogoImage(): HTMLImageElement | null {
  if (_logoReady && _logoImg) return _logoImg;
  if (_logoRequested || typeof document === "undefined") return null;
  _logoRequested = true;
  const img = new Image();
  img.decoding = "async";
  img.onload = () => {
    _logoImg = img;
    _logoReady = true;
    _repaintHook?.();
  };
  img.onerror = () => { _logoRequested = false; };
  img.src = "/cosmic/grid-logo.png";
  return null;
}

/** Procedural four-ambience fallback until the raster decodes. */
function drawNebulaFallback(
  ctx: CanvasRenderingContext2D,
  x: number, y: number, w: number, h: number,
): void {
  const quads: [number, number, string][] = [
    [x,     y,     "rgba(40, 150, 235, 0.32)"], // upper-left  · electric blue
    [x + w, y,     "rgba(190, 70, 220, 0.30)"], // upper-right · violet/magenta
    [x,     y + h, "rgba(240, 150, 50, 0.28)"], // lower-left  · amber/orange
    [x + w, y + h, "rgba(40, 210, 180, 0.28)"], // lower-right · teal/aqua
  ];
  ctx.save();
  ctx.globalCompositeOperation = "lighter";
  const rad = Math.max(w, h) * 0.62;
  for (const [qx, qy, col] of quads) {
    const g = ctx.createRadialGradient(qx, qy, 0, qx, qy, rad);
    g.addColorStop(0, col);
    g.addColorStop(1, "rgba(0, 0, 0, 0)");
    ctx.fillStyle = g;
    ctx.fillRect(x, y, w, h);
  }
  ctx.restore();
}

/** Thin luminous cyan perimeter with brighter corner brackets (sharp). */
/** Bright RESERVE corner-dot colour, tinted by which grid quadrant the
 *  point (world coords) falls in — matching the four nebula ambiences:
 *  UL electric blue, UR violet, LL amber/orange, LR emerald. */
function quadrantDotColor(wx: number, wy: number): string {
  const right = wx >= 500;
  const bottom = wy >= 500;
  if (!right && !bottom) return "rgba(96, 200, 255, 0.95)";  // UL · electric blue
  if (right && !bottom) return "rgba(196, 140, 255, 0.95)";  // UR · violet
  if (!right && bottom) return "rgba(255, 176, 74, 0.95)";   // LL · amber/orange
  return "rgba(74, 236, 176, 0.95)";                         // LR · emerald
}

function drawGridPerimeter(
  ctx: CanvasRenderingContext2D,
  x: number, y: number, w: number, h: number, r: number, fast = false,
): void {
  ctx.save();
  // WO-CONTEST §PERIMETER-OFF (Claudio) — the 1000×1000 grid's own perimeter
  // GLOW is removed ENTIRELY: the broad diffuse halo (the "ombreggiatura
  // perimetrale") is gone. The thin crisp edge line that defines the plane
  // contour is KEPT in place but rendered INVISIBLE (fully transparent), so
  // the plane has no visible border/glow of its own. The 3 concentric
  // perimeter bands (drawn in render() §2, band-fade) still carry the edge
  // light and now fade smoothly with camera motion.
  void fast;
  ctx.strokeStyle = "rgba(140, 226, 252, 0)"; // invisible — kept per Claudio
  ctx.lineWidth = 1.5;
  strokeRoundedRect(ctx, Math.round(x) + 0.5, Math.round(y) + 0.5, Math.round(w), Math.round(h), r);
  ctx.restore();
}

/** Boulevard delineation: luminous cyan lines along the TWO EDGES of each
 *  50-wide central corridor (not the centre), running from the perimeter to
 *  a central OCTAGON that frames the nexus. Square nodes follow the edges.
 *  All locked to the authoritative world centre (500,500). */
function drawCentralAxes(
  ctx: CanvasRenderingContext2D,
  camera: Camera,
): void {
  const w2s = (wx: number, wy: number) => worldToScreen(camera, wx, wy);

  // Fade the SQUARE CORNERS of the underlying hub that protrude past the
  // octagon's diagonal chamfers → 80% transparent (Claudio). Drawn first so
  // the octagon edges stay crisp on top.
  const cornerTris: [number, number][][] = [
    [[350, 350], [430, 350], [350, 430]],
    [[650, 350], [570, 350], [650, 430]],
    [[650, 650], [570, 650], [650, 570]],
    [[350, 650], [430, 650], [350, 570]],
  ];
  ctx.save();
  ctx.fillStyle = "rgba(6, 12, 22, 0.9)";
  for (const tri of cornerTris) {
    ctx.beginPath();
    for (let i = 0; i < tri.length; i++) {
      const s = w2s(tri[i][0], tri[i][1]);
      if (i === 0) ctx.moveTo(s.x, s.y); else ctx.lineTo(s.x, s.y);
    }
    ctx.closePath();
    ctx.fill();
  }
  ctx.restore();

  // World geometry (Claudio): corridors at x=475/525 & y=475/525; hub 350..650.
  const polys: [number, number][][] = [
    // Horizontal boulevard edges (y = 475 / 525), perimeter → hub
    [[0, 475], [350, 475]], [[650, 475], [1000, 475]],
    [[0, 525], [350, 525]], [[650, 525], [1000, 525]],
    // Vertical boulevard edges (x = 475 / 525), perimeter → hub
    [[475, 0], [475, 350]], [[475, 650], [475, 1000]],
    [[525, 0], [525, 350]], [[525, 650], [525, 1000]],
    // Octagon — 4 diagonal chamfers around the nexus
    [[350, 430], [430, 350]], [[570, 350], [650, 430]],
    [[650, 570], [570, 650]], [[430, 650], [350, 570]],
    // Octagon — shoulders on the axis-aligned sides
    [[430, 350], [475, 350]], [[525, 350], [570, 350]],
    [[650, 430], [650, 475]], [[650, 525], [650, 570]],
    [[570, 650], [525, 650]], [[475, 650], [430, 650]],
    [[350, 570], [350, 525]], [[350, 475], [350, 430]],
  ];
  // Boulevard mouths CONVERGE into inward chevrons pointing at the nexus.
  // Drawn at −40% glow (Claudio) so the shape reads more like a clean octagon
  // than an octagon with 4 deep notches.
  const chevrons: [number, number][][] = [
    [[475, 350], [500, 392], [525, 350]],
    [[650, 475], [608, 500], [650, 525]],
    [[525, 650], [500, 608], [475, 650]],
    [[350, 525], [392, 500], [350, 475]],
  ];

  const strokeList = (list: [number, number][][]) => {
    for (const poly of list) {
      ctx.beginPath();
      for (let i = 0; i < poly.length; i++) {
        const s = w2s(poly[i][0], poly[i][1]);
        if (i === 0) ctx.moveTo(s.x, s.y); else ctx.lineTo(s.x, s.y);
      }
      ctx.stroke();
    }
  };

  ctx.save();
  ctx.lineJoin = "round";
  ctx.lineCap = "round";
  // Soft glow underlay. Main octagon + boulevards at full; inward chevrons at
  // −40% so they read as a subtle notch (cleaner octagon look — Claudio).
  ctx.globalCompositeOperation = "lighter";
  for (let i = 3; i >= 1; i--) {
    ctx.lineWidth = i * 4;
    ctx.strokeStyle = `rgba(46, 182, 228, ${0.00688128 * i})`;   // glow −20% · −30% (Claudio Jun26)
    strokeList(polys);
    ctx.strokeStyle = `rgba(46, 182, 228, ${0.004386816 * i})`;  // glow −20% · −30%
    strokeList(chevrons);
  }
  ctx.globalCompositeOperation = "source-over";
  // Crisp bright edges — octagon −20%, chevrons a further −15%.
  ctx.lineWidth = 2;
  ctx.strokeStyle = "rgba(158, 238, 255, 0.146944)";
  strokeList(polys);
  ctx.strokeStyle = "rgba(158, 238, 255, 0.0936768)";
  strokeList(chevrons);
  ctx.restore();

  // Square nodes along the boulevard edges (every 100 world units, outside the
  // hub) — the glow tick markers follow the new edge trajectories.
  const nh = Math.max(2.5, Math.min(5, camera.scale * 2.2));
  const drawNode = (sx: number, sy: number) => {
    ctx.fillStyle = "rgba(8, 16, 26, 0.95)";
    ctx.fillRect(sx - nh, sy - nh, nh * 2, nh * 2);
    // Nodes brightened +20% (Claudio): 0.304 → 0.365.
    ctx.strokeStyle = "rgba(158, 238, 255, 0.365)";
    ctx.lineWidth = 1;
    ctx.strokeRect(sx - nh + 0.5, sy - nh + 0.5, nh * 2 - 1, nh * 2 - 1);
  };
  const along = [100, 200, 300, 700, 800, 900];
  for (const g of along) {
    let s = w2s(g, 475); drawNode(Math.round(s.x), Math.round(s.y));
    s = w2s(g, 525); drawNode(Math.round(s.x), Math.round(s.y));
    s = w2s(475, g); drawNode(Math.round(s.x), Math.round(s.y));
    s = w2s(525, g); drawNode(Math.round(s.x), Math.round(s.y));
  }
  // Octagon corner nodes (Claudio) — one node at each of the 8 vertices where
  // the diagonal chamfers meet the axis-aligned sides of the framing octagon.
  const octCorners: [number, number][] = [
    [430, 350], [570, 350], [650, 430], [650, 570],
    [570, 650], [430, 650], [350, 570], [350, 430],
  ];
  for (const [wx, wy] of octCorners) {
    const s = w2s(wx, wy);
    drawNode(Math.round(s.x), Math.round(s.y));
  }
}

/** Compact central nexus — dark inner square, cyan tech border, restrained
 *  glow, and The Grid emblem, mathematically centred on (500,500). */
function drawCentralNexus(
  ctx: CanvasRenderingContext2D,
  camera: Camera,
  worldW: number,
  worldH: number,
  revealGridZero = false,
  pulse = 0,
): void {
  const c = worldToScreen(camera, 500, 500);
  const size = Math.max(24, Math.min(66, Math.min(worldW, worldH) * 0.075));
  const half = size / 2;
  const nexR = Math.max(3, size * 0.2);
  // Outer glow (−40%). Cyan by default; red + pulsing when the Grid Zero
  // legend category is being revealed.
  ctx.save();
  ctx.globalCompositeOperation = "lighter";
  const g = ctx.createRadialGradient(c.x, c.y, half * 0.4, c.x, c.y, half * 2.6);
  if (revealGridZero) {
    const gi = 0.55 + 0.45 * pulse;
    g.addColorStop(0,   `rgba(255, 96, 104, ${0.55 * gi})`);
    g.addColorStop(0.5, `rgba(236, 44, 62, ${0.2 * gi})`);
    g.addColorStop(1,   "rgba(236, 44, 62, 0)");
  } else {
    g.addColorStop(0,   "rgba(46, 196, 238, 0.04375)");          // central logo glow −50% · −50% (Claudio Jun26)
    g.addColorStop(0.5, "rgba(34, 152, 214, 0.0147)");           // central logo glow −50% · −50%
    g.addColorStop(1,   "rgba(34, 152, 214, 0)");
  }
  ctx.fillStyle = g;
  ctx.fillRect(c.x - half * 2.6, c.y - half * 2.6, half * 5.2, half * 5.2);
  ctx.restore();
  // Dark inner body (rounded corners).
  ctx.fillStyle = "rgba(6, 14, 24, 0.98)";
  fillRoundedRect(ctx, c.x - half, c.y - half, size, size, nexR);
  // Tech border (double frame, rounded) — cyan default / red on reveal.
  const borderOuter = revealGridZero ? "rgba(255, 128, 136, 0.95)" : "rgba(134, 238, 255, 0.9)";
  const borderInner = revealGridZero ? "rgba(236, 84, 102, 0.7)" : "rgba(92, 204, 238, 0.55)";
  ctx.strokeStyle = borderOuter;
  ctx.lineWidth = 2;
  strokeRoundedRect(ctx, Math.round(c.x - half) + 0.5, Math.round(c.y - half) + 0.5, Math.round(size), Math.round(size), nexR);
  const inset = Math.max(3, size * 0.14);
  ctx.strokeStyle = borderInner;
  ctx.lineWidth = 1;
  strokeRoundedRect(
    ctx,
    Math.round(c.x - half + inset) + 0.5,
    Math.round(c.y - half + inset) + 0.5,
    Math.round(size - inset * 2),
    Math.round(size - inset * 2),
    Math.max(2, nexR - inset * 0.5),
  );
  // The Grid official icon logo (icon-only, no wordmark) centred on the
  // nexus, replacing the procedural quatrefoil. Falls back to the quatrefoil
  // until the PNG decodes.
  const logo = getGridLogoImage();
  if (logo) {
    const L = size * 0.92;
    ctx.drawImage(logo, c.x - L / 2, c.y - L / 2, L, L);
  } else {
    drawNexusEmblem(ctx, c.x, c.y, size * 0.30);
  }
  // WO-VRD-33 (Claudio) — additive red wash over the nexus so the logo/core
  // itself glows red while "Grid Zero" is revealed (the reveal fill alone is
  // hidden beneath this geometry).
  if (revealGridZero) {
    ctx.save();
    ctx.globalCompositeOperation = "lighter";
    ctx.globalAlpha = 0.5 + 0.35 * pulse;
    const rg = ctx.createRadialGradient(c.x, c.y, 0, c.x, c.y, half * 1.15);
    rg.addColorStop(0,   "rgba(255, 96, 104, 0.9)");
    rg.addColorStop(0.6, "rgba(255, 70, 84, 0.35)");
    rg.addColorStop(1,   "rgba(255, 70, 84, 0)");
    ctx.fillStyle = rg;
    fillRoundedRect(ctx, c.x - half, c.y - half, size, size, nexR);
    ctx.restore();
  }
}

/** Four-petal quatrefoil emblem in cyan, centred at (cx,cy). */
function drawNexusEmblem(
  ctx: CanvasRenderingContext2D,
  cx: number, cy: number, r: number,
): void {
  ctx.save();
  ctx.strokeStyle = "rgba(158, 240, 255, 0.95)";
  ctx.fillStyle = "rgba(60, 200, 240, 0.30)";
  ctx.lineWidth = 1.4;
  ctx.lineJoin = "round";
  const diagonals = [Math.PI / 4, (3 * Math.PI) / 4, (5 * Math.PI) / 4, (7 * Math.PI) / 4];
  for (const ang of diagonals) {
    const dx = Math.cos(ang), dy = Math.sin(ang);
    const nx = -dy, ny = dx;
    const base = r * 0.26, tip = r * 1.05, wide = r * 0.36;
    const mx = cx + dx * (base + tip) / 2;
    const my = cy + dy * (base + tip) / 2;
    ctx.beginPath();
    ctx.moveTo(cx + dx * base, cy + dy * base);
    ctx.lineTo(mx + nx * wide, my + ny * wide);
    ctx.lineTo(cx + dx * tip, cy + dy * tip);
    ctx.lineTo(mx - nx * wide, my - ny * wide);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
  }
  // Centre diamond.
  ctx.beginPath();
  ctx.moveTo(cx, cy - r * 0.34);
  ctx.lineTo(cx + r * 0.34, cy);
  ctx.lineTo(cx, cy + r * 0.34);
  ctx.lineTo(cx - r * 0.34, cy);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();
  ctx.restore();
}
